import { httpClient } from './http-client';
import {
  Snapshot,
  SnapshotWithAccount,
  CreateSnapshotRequest,
  BulkCreateSnapshotsRequest,
  UpdateSnapshotRequest,
  SnapshotFilter,
  NetWorthSummaryDetailed,
  NetWorthHistoryPoint,
  AccountHistoryPoint,
  MonthlySnapshot,
  ApiResponse,
  PaginatedResponse,
} from '../types/api';

export class SnapshotsService {
  private static readonly ENDPOINTS = {
    SNAPSHOTS: '/snapshots',
    SNAPSHOT_BY_ID: (id: string) => `/snapshots/${id}`,
    BULK_SNAPSHOTS: '/snapshots/bulk',
    NET_WORTH_SNAPSHOT: '/net-worth/snapshot',
    NET_WORTH_HISTORY: '/net-worth/history',
    // Ledger-derived series. Reconciles by construction; see
    // getLedgerNetWorthHistory for why this is preferred over the snapshot one.
    NET_WORTH_LEDGER: '/net-worth/ledger',
    ACCOUNT_HISTORY: (accountId: string) => `/accounts/${accountId}/history`,
    MONTHLY_SNAPSHOTS: '/snapshots/monthly',
  } as const;

  /**
   * Create a new snapshot
   */
  async createSnapshot(snapshotData: CreateSnapshotRequest): Promise<ApiResponse<Snapshot>> {
    return httpClient.post(SnapshotsService.ENDPOINTS.SNAPSHOTS, snapshotData);
  }

  /**
   * Create multiple snapshots at once
   */
  async bulkCreateSnapshots(request: BulkCreateSnapshotsRequest): Promise<ApiResponse<Snapshot[]>> {
    return httpClient.post(SnapshotsService.ENDPOINTS.BULK_SNAPSHOTS, request);
  }

  /**
   * Get all snapshots with optional filtering and pagination
   */
  async getSnapshots(filter?: SnapshotFilter): Promise<ApiResponse<PaginatedResponse<Snapshot>>> {
    const searchParams = new URLSearchParams();
    
    if (filter) {
      if (filter.account_id) searchParams.set('account_id', filter.account_id);
      if (filter.start_date) searchParams.set('start_date', filter.start_date);
      if (filter.end_date) searchParams.set('end_date', filter.end_date);
      if (filter.page) searchParams.set('page', filter.page.toString());
      if (filter.per_page) searchParams.set('per_page', filter.per_page.toString());
    }

    const url = searchParams.toString() 
      ? `${SnapshotsService.ENDPOINTS.SNAPSHOTS}?${searchParams.toString()}`
      : SnapshotsService.ENDPOINTS.SNAPSHOTS;

    return httpClient.get(url);
  }

  /**
   * Get snapshots with account information
   */
  async getSnapshotsWithAccounts(filter?: SnapshotFilter): Promise<ApiResponse<PaginatedResponse<SnapshotWithAccount>>> {
    // This would need to be a different endpoint or the same endpoint with an include parameter
    const searchParams = new URLSearchParams();
    
    if (filter) {
      if (filter.account_id) searchParams.set('account_id', filter.account_id);
      if (filter.start_date) searchParams.set('start_date', filter.start_date);
      if (filter.end_date) searchParams.set('end_date', filter.end_date);
      if (filter.page) searchParams.set('page', filter.page.toString());
      if (filter.per_page) searchParams.set('per_page', filter.per_page.toString());
    }

    searchParams.set('include', 'account');

    const url = `${SnapshotsService.ENDPOINTS.SNAPSHOTS}?${searchParams.toString()}`;
    const rawResponse = await httpClient.get<any>(url);
    
    // Backend returns: { snapshots: [], total: number, page: number, per_page: number }
    // Handle null/undefined response
    if (!rawResponse) {
      return {
        data: {
          data: [],
          total: 0,
          page: 1,
          perPage: 20,
          totalPages: 0,
        },
      };
    }
    
    // Wrap it to match the expected ApiResponse<PaginatedResponse<T>> format
    return {
      data: {
        data: rawResponse.snapshots || [],
        total: rawResponse.total || 0,
        page: rawResponse.page || 1,
        perPage: rawResponse.per_page || 20,
        totalPages: rawResponse.per_page ? Math.ceil((rawResponse.total || 0) / rawResponse.per_page) : 0,
      },
    };
  }

  /**
   * Get a specific snapshot by ID
   */
  async getSnapshot(id: string): Promise<ApiResponse<Snapshot>> {
    return httpClient.get(SnapshotsService.ENDPOINTS.SNAPSHOT_BY_ID(id));
  }

  /**
   * Update an existing snapshot
   */
  async updateSnapshot(id: string, updates: UpdateSnapshotRequest): Promise<ApiResponse<Snapshot>> {
    return httpClient.put(SnapshotsService.ENDPOINTS.SNAPSHOT_BY_ID(id), updates);
  }

  /**
   * Delete a snapshot
   */
  async deleteSnapshot(id: string): Promise<ApiResponse<void>> {
    return httpClient.delete(SnapshotsService.ENDPOINTS.SNAPSHOT_BY_ID(id));
  }

  /**
   * Get net worth snapshot for a specific date
   */
  async getNetWorthSnapshot(date?: string): Promise<ApiResponse<NetWorthSummaryDetailed>> {
    const url = date 
      ? `${SnapshotsService.ENDPOINTS.NET_WORTH_SNAPSHOT}?date=${date}`
      : SnapshotsService.ENDPOINTS.NET_WORTH_SNAPSHOT;
    
    return httpClient.get(url);
  }

  /**
   * Get net worth history over a date range
   */
  async getNetWorthHistory(
    startDate: string,
    endDate: string
  ): Promise<ApiResponse<NetWorthHistoryPoint[]>> {
    const searchParams = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
    });

    const url = `${SnapshotsService.ENDPOINTS.NET_WORTH_HISTORY}?${searchParams.toString()}`;
    const raw = await httpClient.get<NetWorthHistoryPoint[] | ApiResponse<NetWorthHistoryPoint[]>>(url);
    const data: NetWorthHistoryPoint[] = (raw as any)?.data ? (raw as any).data : (raw as NetWorthHistoryPoint[]);
    return { data };
  }

  /**
   * Get account value history over a date range
   */
  async getAccountHistory(
    accountId: string,
    startDate: string,
    endDate: string
  ): Promise<ApiResponse<AccountHistoryPoint[]>> {
    const searchParams = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
    });

    const url = `${SnapshotsService.ENDPOINTS.ACCOUNT_HISTORY(accountId)}?${searchParams.toString()}`;
    return httpClient.get(url);
  }

  /**
   * Get monthly net worth snapshots
   */
  async getMonthlySnapshots(): Promise<ApiResponse<MonthlySnapshot[]>> {
    return httpClient.get(SnapshotsService.ENDPOINTS.MONTHLY_SNAPSHOTS);
  }

  /**
   * Get snapshots for a specific account
   */
  async getAccountSnapshots(
    accountId: string,
    limit?: number,
    offset?: number
  ): Promise<ApiResponse<Snapshot[]>> {
    return this.getSnapshots({
      account_id: accountId,
      page: offset ? Math.floor(offset / (limit || 20)) + 1 : 1,
      per_page: limit || 20,
    }).then(response => ({
      data: response.data.data,
      message: response.message,
      error: response.error,
    }));
  }

  /**
   * Get latest snapshot for each account
   */
  async getLatestSnapshots(): Promise<ApiResponse<SnapshotWithAccount[]>> {
    // This would need specific backend support
    const searchParams = new URLSearchParams({
      latest: 'true',
      include: 'account',
    });

    const url = `${SnapshotsService.ENDPOINTS.SNAPSHOTS}?${searchParams.toString()}`;
    return httpClient.get(url);
  }

  /**
   * Check if a snapshot exists for an account on a specific date
   */
  async checkSnapshotExists(accountId: string, date: string): Promise<boolean> {
    try {
      const response = await this.getSnapshots({
        account_id: accountId,
        start_date: date,
        end_date: date,
        per_page: 1,
      });
      return response.data.data.length > 0;
    } catch {
      return false;
    }
  }

  /**
   * Get snapshot statistics
   */
  async getSnapshotStats(): Promise<{
    totalSnapshots: number;
    snapshotsThisMonth: number;
    snapshotsThisYear: number;
    oldestSnapshot?: string;
    newestSnapshot?: string;
  }> {
    const now = new Date();
    const thisMonth = now.toISOString().slice(0, 7); // YYYY-MM format
    const thisYear = now.getFullYear().toString();

    const [allSnapshots, monthlySnapshots, yearlySnapshots] = await Promise.allSettled([
      this.getSnapshots({ per_page: 1 }),
      this.getSnapshots({ 
        start_date: `${thisMonth}-01`,
        end_date: `${thisMonth}-31`,
        per_page: 1000,
      }),
      this.getSnapshots({ 
        start_date: `${thisYear}-01-01`,
        end_date: `${thisYear}-12-31`,
        per_page: 1000,
      }),
    ]);

    const stats = {
      totalSnapshots: 0,
      snapshotsThisMonth: 0,
      snapshotsThisYear: 0,
      oldestSnapshot: undefined as string | undefined,
      newestSnapshot: undefined as string | undefined,
    };

    if (allSnapshots.status === 'fulfilled') {
      stats.totalSnapshots = allSnapshots.value.data.total;
    }

    if (monthlySnapshots.status === 'fulfilled') {
      stats.snapshotsThisMonth = monthlySnapshots.value.data.data.length;
    }

    if (yearlySnapshots.status === 'fulfilled') {
      stats.snapshotsThisYear = yearlySnapshots.value.data.data.length;
      
      const snapshots = yearlySnapshots.value.data.data;
      if (snapshots.length > 0) {
        // Sort by date to find oldest and newest
        snapshots.sort((a, b) => a.date.localeCompare(b.date));
        stats.oldestSnapshot = snapshots[0].date;
        stats.newestSnapshot = snapshots[snapshots.length - 1].date;
      }
    }

    return stats;
  }

  /**
   * Import snapshots from CSV data
   */
  async importSnapshots(csvData: string): Promise<ApiResponse<{ imported: number; errors: string[] }>> {
    // This would need backend support for CSV parsing
    // For now, we'll parse CSV client-side and use bulk create
    
    const lines = csvData.trim().split('\n');
    const headers = lines[0].split(',').map(h => h.trim());
    
    // Expected headers: account_id, date, balance, description, source
    const requiredHeaders = ['account_id', 'date', 'balance'];
    const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
    
    if (missingHeaders.length > 0) {
      throw new Error(`Missing required headers: ${missingHeaders.join(', ')}`);
    }

    const snapshots: CreateSnapshotRequest[] = [];
    const errors: string[] = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      const row: Record<string, string> = {};
      
      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });

      try {
        if (!row.account_id || !row.date || !row.balance) {
          throw new Error(`Missing required data in row ${i + 1}`);
        }

        snapshots.push({
          account_id: row.account_id,
          date: row.date,
          balance: row.balance,
          description: row.description || '',
          source: row.source || 'CSV Import',
        });
      } catch (error) {
        errors.push(`Row ${i + 1}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }

    if (snapshots.length === 0) {
      throw new Error('No valid snapshots found in CSV');
    }

    try {
      await this.bulkCreateSnapshots({ snapshots });
      return {
        data: { imported: snapshots.length, errors },
      };
    } catch (error) {
      return {
        data: { imported: 0, errors: [...errors, error instanceof Error ? error.message : 'Import failed'] },
      };
    }
  }
  /**
   * Net worth over time, derived from the double-entry ledger.
   *
   * Preferred over getNetWorthHistory, which reads `snapshots` -- a manual,
   * point-in-time table that the Plaid, FX and CSV importers never write to.
   * Those importers post journal entries instead, so the snapshot series
   * silently stops tracking reality the moment anything is imported: the
   * headline read CA$3.37M while the chart above it flatlined around CA$1.4M
   * and trended DOWNWARD through a period when net worth more than doubled.
   *
   * The ledger series is monthly rather than daily. That is coarser than the
   * snapshot line looked, and it is the right trade: a smooth daily curve
   * drawn from stale data is worse than a stepped monthly one drawn from the
   * ledger that reconciles.
   *
   * Debts arrive as a POSITIVE magnitude and are mapped to total_debts
   * unchanged -- the chart negates them itself.
   */
  async getLedgerNetWorthHistory(
    startDate: string,
    endDate: string,
  ): Promise<ApiResponse<NetWorthHistoryPoint[]>> {
    const url = `${SnapshotsService.ENDPOINTS.NET_WORTH_LEDGER}?from=${startDate}&to=${endDate}`;
    const raw = await httpClient.get<any>(url);
    const series: any[] = raw?.series ?? raw?.data?.series ?? [];
    return {
      data: series.map((p) => ({
        date: p.period,
        net_worth: String(p.net_worth ?? '0'),
        total_assets: String(p.assets ?? '0'),
        total_debts: String(p.debts ?? '0'),
        total_investments: String(p.investments ?? '0'),
      })) as NetWorthHistoryPoint[],
    } as ApiResponse<NetWorthHistoryPoint[]>;
  }

}

// Export singleton instance
export const snapshotsService = new SnapshotsService();
